/*
 * Copyright (c) 2024-2026. The content provided herein is protected by copyright law. Unauthorized use, reproduction, or distribution of this material without proper licensing, knowledge, and contract agreement from the author is strictly prohibited. Any infringement of these rights may result in legal action. For inquiries regarding the use of this content, please contact the author for proper authorization and agreement.
 */

import clsx from 'clsx'
import { useTranslation } from 'react-i18next'
import UiFormInner from 'components/Forms/UIFormInner'
import { UiTable, UiTableBody, UiTableCell, UiTableContainer, UiTableHead, UiTableHeaderCell, UiTableRow } from 'components/Ui/UiTable'
import { Reservation } from 'types/entities/Reservation'

interface EditCompanyModalReservationsTabProps {
    reservations: Reservation[]
    onReservationClick?: (reservation: Reservation) => void
}

const EditCompanyModalReservationsTab = ({
    reservations,
    onReservationClick,
}: EditCompanyModalReservationsTabProps) => {
    const { t } = useTranslation()

    const isPastReservation = (reservation: Reservation): boolean => {
        if (!reservation?.date) {
            return false
        }

        const eventDate = new Date(reservation.date)
        if (Number.isNaN(eventDate.getTime())) {
            return false
        }

        return eventDate.getTime() < Date.now()
    }

    return (
        <div className={clsx('EditCompanyModalReservationsTab flex-1 flex flex-col pt-4 overflow-hidden')}>
            <UiFormInner title={t('Bookings')}>
                <UiTableContainer>
                    <UiTable>
                        <UiTableHead>
                            <UiTableHeaderCell align="left">{t('ID')}</UiTableHeaderCell>
                            <UiTableHeaderCell align="left">{t('Date')}</UiTableHeaderCell>
                            <UiTableHeaderCell align="left">{t('Status')}</UiTableHeaderCell>
                        </UiTableHead>
                        <UiTableBody>
                            {reservations.map((reservation) => (
                                <UiTableRow
                                    key={reservation.id}
                                    onClick={() => onReservationClick?.(reservation)}
                                    className={onReservationClick ? 'cursor-pointer' : undefined}
                                >
                                    <UiTableCell>{reservation.id}</UiTableCell>
                                    <UiTableCell>
                                        <div className="flex items-center gap-2">
                                            <span>{reservation.date || '-'}</span>
                                            {isPastReservation(reservation) && (
                                                <span className="text-[10px] font-semibold px-2 py-0.5 rounded bg-gray-100 text-gray-700">
                                                    {t('Past event')}
                                                </span>
                                            )}
                                        </div>
                                    </UiTableCell>
                                    <UiTableCell>{reservation.status || '-'}</UiTableCell>
                                </UiTableRow>
                            ))}
                        </UiTableBody>
                    </UiTable>
                </UiTableContainer>
            </UiFormInner>
        </div>
    )
}

export default EditCompanyModalReservationsTab
